import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';

import { DocumentViewerModal } from 'src/Components/Modals/DocumentViewerModal';
import { CompactFileList } from 'src/Components/Shared/CompactFileList';
import { VideoPlayer } from 'src/Components/Video/VideoPlayer';
import { AttachmentDto } from 'src/Services/API/generated';

import styles from './AttachmentsViewer.module.scss';
import { Props } from './AttachmentsViewer.types';

const AttachmentsViewer = ({ attachments, openFirstOnMount = false }: Props) => {
  const [selectedAttachment, setSelectedAttachment] = useState<AttachmentDto | null>(null);
  const [modalOpen, { open: openModal, close: closeModal }] = useDisclosure();

  useEffect(() => {
    if (!openFirstOnMount) return;
    const first = attachments[0];
    if (!first) return;
    setSelectedAttachment(first);
    openModal();
    // Mount-only auto-open: firing this on every attachments change would fight
    // the user closing the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attachmentsWithPreview = attachments.filter(
    attachment => attachment.urls?.previewMediumUrl
  );

  const attachmentsWithoutPreview = attachments.filter(
    attachment => attachment.urls?.previewMediumUrl === undefined && !attachment.video
  );

  const videoAttachments = attachments.filter(attachment => !!attachment.video);

  const handleAttachmentClick = (attachment: AttachmentDto) => {
    setSelectedAttachment(attachment);
    openModal();
  };

  return (
    <div className={styles.container}>
      <div className={styles.previewList}>
        {attachmentsWithPreview.map(attachment => (
          <img
            key={attachment.id}
            src={attachment.urls.previewMediumUrl}
            className={styles.preview}
            onClick={() => handleAttachmentClick(attachment)}
          />
        ))}

        {attachmentsWithoutPreview.length > 0 && (
          <CompactFileList
            files={attachmentsWithoutPreview.map(attachment => ({
              id: attachment.id,
              name: attachment.name,
              publicUrl: attachment.urls.fileUrl,
            }))}
            onFileClick={fileId => {
              const attachment = attachmentsWithoutPreview.find(a => a.id === fileId);
              if (attachment) {
                handleAttachmentClick(attachment);
              }
            }}
          />
        )}

        {videoAttachments.map(attachment => {
          if (attachment.video) {
            return (
              <div
                key={attachment.id}
                className={styles.videoWrapper}
                onClick={() => handleAttachmentClick(attachment)}
              >
                <VideoPlayer video={attachment.video} />
              </div>
            );
          }
        })}
      </div>

      <DocumentViewerModal
        open={modalOpen}
        fileUrl={
          selectedAttachment?.urls?.previewLargeUrl ?? selectedAttachment?.urls?.fileUrl ?? ''
        }
        fileName={selectedAttachment?.name ?? ''}
        video={selectedAttachment?.video}
        onClose={closeModal}
      />
    </div>
  );
};

export default AttachmentsViewer;
